import React, { useState, useEffect, useRef } from "react";
import "./shagirdkids.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards, EffectCoverflow } from "swiper/modules";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Card from "./components/Card";
import KidService from "../../services/kids";
import "swiper/css";
import "swiper/css/effect-coverflow";
import KidSponsorModal from "./components/KidSponsorModal";
import Category from "./components/Category";
import CategoryService from "../../services/categories";
import { useNavigate } from "react-router-dom";
import KidProfileModal from "./components/KidProfileModal";
import Stepper from "./components/Stepper";
import MobileFooter from "../../components/MobileFooter";
import MobileCard from "./components/MobileCard";
import { useSponsorResumeReopen } from "../../utils/sponsorResume";
import { filterKidsForSponsorListing } from "../../utils/kidAvailability";

const ShaagirdKidsComponent = () => {
  const [activeTab, setActiveTab] = useState("Charity");
  const [kidsData, setKidsData] = useState([]);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedKidId, setSelectedKidId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [filteredKids, setFilteredKids] = useState([]);
  const [page, setPage] = useState(0);
  const [mobileCategorySlide, setMobileCategorySlide] = useState(0);
  const cardsPerPage = 4;
  const navigate = useNavigate();
  const categoryRowRef = useRef(null);

  useSponsorResumeReopen(setSelectedKidId, setShowSponsorModal);

  // Memoize hero kids for Swiper (top 10 priority or first 10 unsponsored)
  const heroKids = React.useMemo(() => {
    const priority = kidsData.filter((k) => k.is_priority);
    if (priority.length > 0) return priority.slice(0, 30);
    return kidsData.slice(0, 20);
  }, [kidsData]);

  // Define the desired category order
  const categoryOrder = [
    "Mob Lynching",
    "Police Atrocity",
    "Communal Violence",
    "Demolition",
    "Orphans",
    "Refugees",
    "Hafiz",
    "Impoverish",
    "Disaster",
  ];

  // Function to sort categories according to the specified order
  const sortCategoriesByOrder = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];

    return categories.sort((a, b) => {
      const nameA = a.category_name || a.name || "";
      const nameB = b.category_name || b.name || "";

      const indexA = categoryOrder.indexOf(nameA);
      const indexB = categoryOrder.indexOf(nameB);

      // If both categories are in the order list, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in the order list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is in the order list, maintain original order
      return 0;
    });
  };

  // Function to get count for each category
  const getCategoryCount = (categoryId) => {
    if (!kidsData || !Array.isArray(kidsData)) return 0;

    if (categoryId === null) {
      // Return total count for "All kids" button
      return kidsData.length;
    }

    // Count kids that belong to this category
    return kidsData.filter((kid) => {
      // Handle different possible property names for category ID
      const kidCategoryId =
        kid.category_id || kid.categoryId || kid.category?.id;
      return kidCategoryId === categoryId;
    }).length;
  };

  useEffect(() => {
    const fetchKids = async () => {
      try {
        const { data } = await KidService.fetchKids({
          availableForSponsorship: true,
        });

        const availableKids = filterKidsForSponsorListing(data.data);

        // Filter for priority kids by default
        const priorityKids = availableKids.filter((k) => k.is_priority);

        setKidsData(availableKids);
        setFilteredKids(priorityKids.length > 0 ? priorityKids : availableKids);
        setActiveIndex(-1); // Priority tab active by default
      } catch (err) {
        console.log("Error fetching kids:", err);
        setKidsData([]);
        setFilteredKids([]);
        setActiveIndex(-1);
      }
    };
    fetchKids();
  }, []);

  useEffect(() => {
    getAllCategories();
  }, []);

  useEffect(() => {
    setFilteredKids(kidsData);
  }, [kidsData]);

  const getAllCategories = async () => {
    try {
      const { data } = await CategoryService.fetchCategories();
      setCategories(data.data);
    } catch (err) {}
  };

  const handleCategory = (e, categoryId, index) => {
    setActiveIndex(index);
    setPage(0);
    if (categoryId) {
      setFilteredKids(kidsData.filter((kid) => kid.category_id === categoryId));
    } else {
      setFilteredKids(kidsData);
    }
  };

  const handleNext = () => {
    if (page + 4 < filteredKids.length) setPage(page + 1);
  };
  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  // Inline Card for this page only
  const getCategoryBackgroundColor = (categoryName) => {
    if (!categoryName) return "#D3B1FF";
    const category = categoryName.toLowerCase();
    if (category === "hafiz") return "#a4b465";
    if (category === "communal violence") return "#fca470";
    if (category === "orphans") return "#94b088";
    if (category === "mob lynching") return "#e27878";
    if (category === "police atrocity") return "#F6EFBD";
    if (category === "impoverish") return "#bd90a9";
    if (category === "demolition") return "#96b1ab";
    if (category === "refugees") return "#bda887";
    if (category === "disaster") return "#ABE0F0";
    return "#bda887";
  };

  const handleSponsor = (kidId) => {
    setSelectedKidId(kidId);
    setShowSponsorModal(true);
  };
  const handleProfile = (kidId) => {
    setSelectedKidId(kidId);
    setShowProfileModal(true);
  };
  const closeSponsorModal = () => {
    setShowSponsorModal(false);
    setSelectedKidId(null);
  };
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedKidId(null);
  };
  const CustomKidCard = ({ data, onSponsor }) => {
    // Age calculation - current year minus birth year
    let age = "";
    if (data.date_of_birth) {
      const birthYear = new Date(data.date_of_birth).getFullYear();
      const currentYear = new Date().getFullYear();
      age = `${currentYear - birthYear}Y`;
    }
    // Remove address logic
    const state = data.state;
    const bgColor = getCategoryBackgroundColor(data.category_name);
    return (
      <div
        style={{
          background: bgColor,
          borderRadius: "30px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.07)",
          padding: 0,
          width: 279,
          minHeight: 452,
          height: 452,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          justifyContent: "flex-end",
          overflow: "hidden",
          transition: "none",
          WebkitTapHighlightColor: "transparent",
          touchAction: "pan-y",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
        className="custom-kid-card"
      >
        {/* Image area */}
        <div
          style={{
            width: "100%",
            height: 255,
            background: data.avatar_url ? "none" : "#e6f5e6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
        >
          {data.avatar_url && (
            <img
              src={data.avatar_url}
              alt={data.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 0,
                pointerEvents: "none",
              }}
            />
          )}
        </div>
        {/* Card content */}
        <div
          style={{
            width: "100%",
            padding: "0rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            marginTop: 256,
            height: "176px",
            pointerEvents: "none",
          }}
        >
          {/* Name */}
          <div
            style={{
              fontWeight: 550,
              fontSize: 28,
              color: "#293E4F",
              marginTop: 0,
              marginBottom: 20,
              textAlign: "left",
              fontFamily: "Poppins",
              width: "100%",
              lineHeight: 1.1,
              alignItems: "center",
              textAlign: "center",
              justifyContent: "center",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            {data.name}
            &nbsp;
            <span style={{ fontSize: 14, color: "#293E4F", fontWeight: "300" }}>
              {age && <span>{age}</span>}
              {age && state && (
                <span style={{ margin: "0 2px", fontSize: 10 }}>, </span>
              )}
              {state && <span>{state}</span>}
            </span>
          </div>
          {/* Category */}
          <div
            style={{
              // background: "rgba(255, 255, 255, 0.8)",
              background: "rgba(255, 255, 255, 0.3)",
              color: "#333",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Inter",
              borderRadius: 24,
              padding: "4px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "fit-content",
              margin: "0 auto 10px auto",
              minHeight: 20,
              boxSizing: "border-box",
              letterSpacing: 0.2,
              pointerEvents: "none",
            }}
          >
            {data.category_name}
          </div>
          {/* Monthly Expenses */}
          <p
            style={{
              color: "#293E4F",
              fontSize: "12px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: "center",
              fontFamily: "Inter",
              margin: "4px auto 12px auto",
              pointerEvents: "none",
            }}
          >
            Monthly Expenses: ₹{data.total_expenses}
          </p>
          {/* Sponsor button - Keep touch events */}
          <button
            style={{
              background: "#293e4f",
              border: "none",
              borderRadius: "40px",
              padding: "8px 4px",
              color: "#fdc512",
              fontWeight: 300,
              fontSize: 16,
              width: "70%",
              margin: "0px auto",
              marginBottom: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "pointer",
              transition: "background 0.2s",
              pointerEvents: "auto",
              fontFamily: "Inter",
            }}
            onClick={() => onSponsor(data.id)}
          >
            Sponsor
          </button>
          {/* View Profile link - Keep touch events */}
          <a
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              textDecoration: "underline",
              fontSize: "12px",
              marginBottom: "10px",
              fontWeight: "400",
              color: "#293E4F",
              pointerEvents: "auto",
            }}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleProfile(data.id);
            }}
          >
            View Profile
          </a>
        </div>
      </div>
    );
  };

  const scrollToCategory = (index) => {
    if (categoryRowRef.current) {
      const buttons = categoryRowRef.current.querySelectorAll(".category-btn");
      if (buttons[index]) {
        buttons[index].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  };

  const totalMobileSlides = categories.length + 1;

  const goToMobileSlide = (index) => {
    const clamped = Math.max(0, Math.min(index, totalMobileSlides - 1));
    setMobileCategorySlide(clamped);
    scrollToCategory(clamped);
  };

  const handleCategoryRowScroll = () => {
    if (!categoryRowRef.current) return;
    const container = categoryRowRef.current;
    const buttons = container.querySelectorAll(".category-btn");
    if (!buttons.length) return;

    const center = container.scrollLeft + container.clientWidth / 2;
    let closestIndex = 0;
    let minDistance = Number.POSITIVE_INFINITY;

    buttons.forEach((btn, idx) => {
      const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
      const distance = Math.abs(btnCenter - center);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = idx;
      }
    });

    setMobileCategorySlide(closestIndex);
  };

  // Empty state component
  const EmptyStateMessage = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "3rem 1rem",
        minHeight: "300px",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        margin: "2rem 0",
        border: "2px dashed #e0e0e0",
      }}
    >
      <div
        style={{
          fontSize: "48px",
          marginBottom: "1rem",
          opacity: 0.6,
        }}
      ></div>
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "600",
          color: "#293E4F",
          marginBottom: "0.5rem",
          fontFamily: "Poppins",
        }}
      >
        All Kids in This Category Are Already Sponsored!
      </h3>
      <p
        style={{
          fontSize: "14px",
          color: "#666",
          marginBottom: "1.5rem",
          maxWidth: "400px",
          lineHeight: "1.5",
          fontFamily: "Inter",
        }}
      >
        Thanks to generous sponsors like you, every child in this category has
        found support. Check out other categories to find more children who need
        your help.
      </p>
      <button
        onClick={() => {
          setActiveIndex(-1);
          setFilteredKids(kidsData);
          setPage(0);
        }}
        style={{
          background: "#299d8e",
          color: "white",
          border: "none",
          borderRadius: "24px",
          padding: "0.75rem 1.5rem",
          fontWeight: "500",
          fontSize: "14px",
          cursor: "pointer",
          transition: "background 0.2s",
          fontFamily: "Inter",
        }}
        onMouseEnter={(e) => (e.target.style.background = "#248a7d")}
        onMouseLeave={(e) => (e.target.style.background = "#299d8e")}
      >
        View All Kids
      </button>
    </div>
  );
  const isMobile = window.innerWidth <= 768;
  const pageSize = isMobile ? 4 : 8;

  return (
    <div className="shaagird-kids-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="yellow-bg">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Empower a Child's Future Through Education</h1>
              <div className="yellow-underline"></div>
              <p>
                Shaagird connects you with children in need, enabling them to
                access education <br />
                and transform lives.
              </p>
              <div
                className="hero-buttons"
                style={{
                  marginLeft: "-17px",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  color: "black",
                }}
              >
                <button
                  className="view-demo-btn"
                  onClick={() =>
                    window.open(
                      "https://www.youtube.com/watch?v=X-t8n0qoJ-Y",
                      "_blank",
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src="./Play.png"
                    style={{
                      height: "34px",
                      width: "34px",
                      paddingRight: "2px",
                    }}
                    alt="Play icon"
                  />
                  <span
                    style={{
                      marginTop: "3px",
                      marginLeft: "10px",
                      textDecoration: "none",
                      color: "black",
                    }}
                  >
                    What is Shaagird?
                  </span>
                </button>
              </div>
            </div>
            <div className="hero-image">
              <div style={{ width: "100%", maxWidth: 400, margin: "0 auto" }}>
                <Swiper
                  effect="cards"
                  grabCursor={true}
                  centeredSlides={true}
                  initialSlide={1}
                  slidesPerView={"auto"}
                  modules={[EffectCards]}
                  observer={true}
                  observeParents={true}
                  style={{ width: "100%", padding: "2rem 0" }}
                >
                  {heroKids.map((data) => (
                    <SwiperSlide
                      key={data.id}
                      style={{
                        width: 260,
                        minWidth: 260,
                        maxWidth: 260,
                        boxSizing: "border-box",
                      }}
                    >
                      <CustomKidCard data={data} onSponsor={handleSponsor} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-container">
          {/* Stats Section */}
          <div className="stats-left">
            <div className="stat-item">
              <h2>500+</h2>
              <p>Children</p>
            </div>
            <div className="stat-item">
              <h2>9+</h2>
              <p>Center</p>
            </div>
            <div className="stat-item">
              <h2>3000+</h2>
              <p>Smiles</p>
            </div>
            <div className="stat-item">
              <h2>6+</h2>
              <p>States</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Story Carousel (only visible on mobile) */}
      <div className="mobile-story-carousel">
        <a
          href="https://stories.shaagird.org/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="./shaagirdnama.png"
            alt="Shaagirdnama"
            style={{
              width: "100%",
              maxWidth: 340,
              height: 220,
              objectFit: "contain",
              borderRadius: 12,
              display: "block",
              margin: "0 auto",
            }}
          />
        </a>
        <a
          href="https://stories.shaagird.org/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <button
            className="show-more-btn"
            style={{
              backgroundColor: "#264653",
              color: "white",
              borderRadius: "2rem",
              padding: "0.5rem 2rem",
              fontWeight: 500,
              fontSize: 16,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              margin: "1rem auto 0",
              display: "block",
              textDecoration: "none",
              textUnderlineOffset: "none",
            }}
          >
            Show More
          </button>
        </a>

        <h2 className="mobile-story-heading">Each Child Has a Story!</h2>
        <p>
          Shagird works on the principle dream of 'Kulsum Sayani' an
          inspirational pioneer worker in the field of literacy.
        </p>
      </div>

      <div style={{ margin: "2rem 0" }}>
        {/* Responsive Category Bar (like AllKids.js) */}
        <div className="row mb-4">
          <div className="col-12">
            {/* Mobile Layout: Horizontal Scrollable Bar */}
            <div className="d-block d-md-none">
              <div style={{ position: "relative", padding: "0 34px" }}>
                <button
                  type="button"
                  aria-label="Scroll categories left"
                  onClick={() => goToMobileSlide(mobileCategorySlide - 1)}
                  style={{
                    position: "absolute",
                    left: "4px",
                    top: "34%",
                    transform: "translateY(-50%)",
                    zIndex: 20,
                    width: 34,
                    height: 34,
                    border: "1.5px solid #2aa89a",
                    background: "#fff",
                    color: "#2aa89a",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                >
                  <FaChevronLeft size={16} />
                </button>

                <div
                  className="category-scroll-row"
                  ref={categoryRowRef}
                  onScroll={handleCategoryRowScroll}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    gap: "10px",
                    padding: "8px 0",
                    WebkitOverflowScrolling: "touch",
                    scrollSnapType: "x mandatory",
                  }}
                >
                  <button
                    onClick={() => {
                      setActiveIndex(-1);
                      setFilteredKids(kidsData);
                      setPage(0);
                      setMobileCategorySlide(0);
                    }}
                    className={`category-btn${activeIndex === -1 ? " active" : ""}`}
                    style={{
                      background: activeIndex === -1 ? "#299d8e" : "#f5f5f5",
                      color: activeIndex === -1 ? "white" : "black",
                      border: "none",
                      borderRadius: 24,
                      padding: "0.5rem 1.5rem",
                      fontWeight: 500,
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      boxShadow:
                        activeIndex === -1
                          ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                          : "none",
                      transition: "all 0.2s",
                      scrollSnapAlign: "start",
                    }}
                  >
                    <span>All kids</span>
                    <span
                      style={{
                        borderRadius: "10px",
                        padding: "2px 6px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        minWidth: "10px",
                        textAlign: "center",
                        lineHeight: "1.2",
                      }}
                    >
                      {getCategoryCount(null)}
                    </span>
                  </button>

                  {categories.map((cat, idx) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveIndex(idx);
                        setFilteredKids(
                          kidsData.filter((kid) => kid.category_id === cat.id),
                        );
                        setPage(0);
                        setMobileCategorySlide(idx + 1);
                      }}
                      className={`category-btn${activeIndex === idx ? " active" : ""}`}
                      style={{
                        background: activeIndex === idx ? "#299d8e" : "#f5f5f5",
                        color: activeIndex === idx ? "#FFF" : "#333",
                        border:
                          activeIndex === idx ? "none" : "1px solid #e1e1df",
                        borderRadius: 24,
                        padding: "0.5rem 1.5rem",
                        fontWeight: 400,
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        boxShadow:
                          activeIndex === idx
                            ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                            : "none",
                        transition: "all 0.2s",
                        scrollSnapAlign: "start",
                      }}
                    >
                      <span>{cat.category_name || cat.name}</span>
                      <span
                        style={{
                          borderRadius: "10px",
                          padding: "2px 6px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          minWidth: "10px",
                          textAlign: "center",
                          lineHeight: "1.2",
                        }}
                      >
                        {getCategoryCount(cat.id)}
                      </span>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  aria-label="Scroll categories right"
                  onClick={() => goToMobileSlide(mobileCategorySlide + 1)}
                  style={{
                    position: "absolute",
                    right: "4px",
                    top: "34%",
                    transform: "translateY(-50%)",
                    zIndex: 20,
                    width: 34,
                    height: 34,
                    border: "1.5px solid #2aa89a",
                    background: "#fff",
                    color: "#2aa89a",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                >
                  <FaChevronRight size={16} />
                </button>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "10px",
                  }}
                >
                  {Array.from({ length: totalMobileSlides }).map((_, index) => {
                    const isActive = mobileCategorySlide === index;
                    return (
                      <button
                        key={index}
                        type="button"
                        aria-label={`Go to category slide ${index + 1}`}
                        onClick={() => goToMobileSlide(index)}
                        style={{
                          width: isActive ? "24px" : "8px",
                          height: "8px",
                          borderRadius: "999px",
                          border: "0",
                          background: isActive ? "#299d8e" : "#b9c4c2",
                          transition: "all 0.2s ease",
                          padding: 0,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Desktop Layout: Row */}
            <div
              className="d-none d-md-flex flex-wrap align-items-center"
              style={{
                gap: "18px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => {
                  setActiveIndex(-1);
                  setFilteredKids(kidsData);
                  setPage(0);
                }}
                className={`category-btn${activeIndex === -1 ? " active" : ""}`}
                style={{
                  backgroundColor: activeIndex === -1 ? "#299d8e" : "#fff",
                  color: activeIndex === -1 ? "#fff" : "#299d8e",
                  border: "2px solid #ececeb",
                  borderRadius: "20px",
                  padding: "0.5rem 2rem 0.5rem 1.5rem",
                  fontWeight: 600,
                  fontSize: 18,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow:
                    activeIndex === -1
                      ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                      : "none",
                  transition: "all 0.2s",
                  marginRight: 8,
                }}
              >
                <span>All Kids</span>
                <span
                  style={{
                    borderRadius: "10px",
                    padding: "2px 6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    minWidth: "10px",
                    textAlign: "center",
                    lineHeight: "1.2",
                  }}
                >
                  {getCategoryCount(null)}
                </span>
              </button>
              {sortCategoriesByOrder(categories).map((cat, idx) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveIndex(idx);
                    setFilteredKids(
                      kidsData.filter((kid) => kid.category_id === cat.id),
                    );
                    setPage(0);
                  }}
                  className={`category-btn${
                    activeIndex === idx ? " active" : ""
                  }`}
                  style={{
                    backgroundColor:
                      activeIndex === idx ? "#299d8e" : "#f5f5f5",
                    color: activeIndex === idx ? "#fff" : "black",
                    border: "2px solid #ececeb",
                    borderRadius: "20px",
                    padding: "0.5rem 2rem 0.5rem 1.5rem",
                    fontWeight: 400,
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow:
                      activeIndex === idx
                        ? "0 2px 8px rgba(252, 193, 24, 0.08)"
                        : "none",
                    transition: "all 0.2s",
                    marginRight: 8,
                  }}
                >
                  <span>{cat.category_name || cat.name}</span>
                  <span
                    style={{
                      background:
                        activeIndex === idx
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.1)",
                      borderRadius: "8px",
                      padding: "1px 4px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      minWidth: "16px",
                      textAlign: "center",
                      lineHeight: "1.1",
                    }}
                  >
                    {getCategoryCount(cat.id)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="children-cards-section">
        {/* Show empty state message if no kids in filtered category */}
        {filteredKids.length === 0 ? (
          <EmptyStateMessage />
        ) : (
          <div className="cards-navigation-container">
            {/* Navigation button - Previous */}
            <button
              className="nav-button prev-button"
              onClick={handlePrev}
              disabled={page === 0}
            >
              <span>{"<"}</span>
            </button>

            {/* Cards Grid */}
            <div className="cards-grid">
              {/* Display 4 cards in a row with increased width */}

              {filteredKids.slice(page, page + pageSize).map((data) => (
                <div className="card-wrapper" key={data.id}>
                  {isMobile ? (
                    <MobileCard
                      data={data}
                      onViewProfile={handleProfile}
                      onSponsor={handleSponsor}
                    />
                  ) : (
                    <Card
                      data={data}
                      onViewProfile={handleProfile}
                      onSponsor={handleSponsor}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Navigation button - Next */}
            <button
              className="nav-button next-button"
              onClick={handleNext}
              disabled={page + 4 >= filteredKids.length}
            >
              <span>{">"}</span>
            </button>
          </div>
        )}

        {/* Only show "See All" button if there are kids to show */}
        {filteredKids.length > 0 && (
          <button
            className="see-all-btn"
            style={{
              margin: "2rem auto",
              display: "block",
              backgroundColor: "#2a3e4f",
              color: "white",
              borderRadius: "2rem",
              padding: "0.5rem 2rem",
              fontWeight: 500,
              fontSize: 18,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            onClick={() => {
              navigate("/all-kids");
              // Scroll to top when navigating to all-kids page
              window.scrollTo(0, 0);
            }}
          >
            See All
          </button>
        )}

        <KidSponsorModal
          isOpen={showSponsorModal}
          onClose={() => {
            setShowSponsorModal(false);
            setSelectedKidId(null);
          }}
          kidId={selectedKidId}
          onBackToProfile={(kidId) => {
            setShowSponsorModal(false);
            setShowProfileModal(true);
            setSelectedKidId(kidId);
          }}
        />
        <KidProfileModal
          isOpen={showProfileModal}
          onClose={closeProfileModal}
          kidId={selectedKidId}
          onSponsorClick={(id) => {
            closeProfileModal();
            setShowSponsorModal(true);
            setSelectedKidId(id);
          }}
        />
      </div>

      {/* Story Section */}
      <div className="story-section">
        <div className="story-content">
          <h2>
            Each Child Has a Story: Discover How Education Is Changing Their
            Lives Forever
          </h2>
          <p>
            Through Shaagird, your generous support empowers resilient young
            dreamers to rise above tough obstacles, pursue bold ambitions, and
            shape their own bright, limitless future with confidence.
          </p>
          <a
            href="https://stories.shaagird.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              className="show-more-btn"
              style={{
                backgroundColor: "#264653",
                color: "white",
                borderRadius: "2rem",
                padding: "0.5rem 2rem",
                fontWeight: 500,
                fontSize: 18,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              Show More
            </button>
          </a>
        </div>

        <div className="story-images">
          <a
            href="https://stories.shaagird.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="./shaagirdnama.png"
              alt="Shaagirdnama"
              style={{
                width: "100%",
                minWidth: 600,
                height: 400,
                objectFit: "contain",
                borderRadius: 12,
                display: "block",
                margin: "0 auto",
              }}
            />
          </a>
        </div>
      </div>
      <h2 className="h2-1">How Shaagird Works?</h2>
      {/* How It Works Section */}
      <div className="how-it-works">
        <Stepper />
      </div>

      {/* Sponsors Section */}
      {/* <div className="sponsors-section">
        <h2>What Sponsors say about us</h2>
        <div className="sponsors-container">
          {[1, 2, 3].map((item) => (
            <div key={item} className="sponsor-card">
              <p>
                Shagird works on the principle dream of 'Kulsum Sayani' an
                inspirational pioneer worker in the field of literacy.
              </p>
              <div className="sponsor-info">
                <div className="sponsor-name">Ayaan Khan</div>
                <div className="sponsor-title">CEO, KMC</div>
              </div>
              <div className="sponsor-image">
                <img src="/kmcc.jpg" alt="Ayaan Khan" />
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/919319986963"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          bottom: "28px",
          right: "28px",
          width: "56px",
          height: "56px",
          backgroundColor: "#25D366",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          zIndex: 9999,
          cursor: "pointer",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.4)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16 2C8.268 2 2 8.268 2 16C2 18.466 2.624 20.792 3.736 22.832L2.032 29.968L9.28 28.288C11.264 29.368 13.552 30 16 30C23.732 30 30 23.732 30 16C30 8.268 23.732 2 16 2ZM16 27.5C13.872 27.5 11.8 26.928 9.976 25.848L9.536 25.584L5.384 26.632L6.456 22.576L6.176 22.128C5.056 20.264 4.464 18.168 4.464 16C4.464 9.632 9.632 4.464 16 4.464C22.368 4.464 27.536 9.632 27.536 16C27.536 22.368 22.368 27.536 16 27.5ZM22.32 19.104C22.016 18.952 20.488 18.184 20.208 18.088C19.928 17.992 19.728 17.944 19.528 18.248C19.328 18.552 18.728 19.264 18.552 19.464C18.376 19.664 18.2 19.688 17.896 19.536C17.592 19.384 16.576 19.056 15.376 17.992C14.44 17.168 13.808 16.152 13.632 15.848C13.456 15.544 13.616 15.384 13.768 15.232C13.904 15.096 14.072 14.872 14.224 14.696C14.376 14.52 14.432 14.392 14.528 14.192C14.624 13.992 14.576 13.816 14.504 13.664C14.432 13.512 13.784 11.984 13.528 11.376C13.28 10.792 13.024 10.872 12.832 10.872C12.656 10.872 12.456 10.872 12.256 10.872C12.056 10.872 11.728 10.944 11.448 11.248C11.168 11.552 10.352 12.32 10.352 13.872C10.352 15.424 11.472 16.928 11.624 17.128C11.776 17.328 13.784 20.408 16.856 21.8C17.576 22.12 18.136 22.312 18.568 22.456C19.296 22.704 19.96 22.672 20.48 22.6C21.064 22.52 22.472 21.8 22.728 21.08C22.984 20.36 22.984 19.752 22.904 19.624C22.824 19.496 22.624 19.256 22.32 19.104Z" />
        </svg>
      </a>

      {/* Add responsive styles for .custom-kid-card */}
      <style>{`
        @media (max-width: 768px) {
          /* Hero section changes */
          .hero-section {
            padding: 1rem;
          }
          
          .hero-content {
            flex-direction: column;
          }
          
          .hero-text {
            width: 100%;
            text-align: center;
          }
          
          .hero-text h1 {
            font-size: 20px;
            margin-bottom: 1rem;
          }
          
          .hero-text p {
            display: none; /* Hide paragraph in mobile view */
          }
          
          .hero-buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
          }
          
          .donate-btn, .view-demo-btn {
            padding: 0.5rem 1rem;
            font-size: 14px;
          }
          
          /* Stats section changes */
          .stats-section {
          
            width: 100%;
            padding: 1rem;
            margin: 0;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          }
          
          .stats-left {
            width: 100%;
            display: flex;
            justify-content: space-between;
          }
          
          .stat-item {
            text-align: center;
          }
          
          .stat-item h2 {
            font-size: 20px;
            margin: 0;
          }
          
          .stat-item p {
            font-size: 12px;
            margin: 0;
          }
          
          .founder-info {
            display: none;
          }
          
          /* Story section changes */
          .story-section {
            padding: 1rem;
          }
          
          .story-content {
            text-align: center;
          }
          
          .story-content h2 {
            font-size: 18px;
          }
          
          .story-content p {
            font-size: 14px;
          }
          
          .image-grid {
            display: flex;
            overflow-x: auto;
            gap: 1rem;
            padding: 1rem 0;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          
          .story-image, .story-image2 {
            flex: 0 0 auto;
            width: 200px;
            height: 200px;
            scroll-snap-align: start;
          }
          
          .story-image img, .story-image2 img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 8px;
          }
          
          /* Category section changes */
          .category-buttons {
            display: flex;
            overflow-x: auto;
            gap: 0.5rem;
            padding: 1rem 0;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          
          .category-buttons button {
            flex: 0 0 auto;
            scroll-snap-align: start;
            font-size: 14px;
            padding: 0.5rem 1rem;
          }
          
          /* Cards section changes */
          .children-cards-section {
            padding: 1rem;
          }
          
          .children-cards-section > div {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
            padding: 1rem 0;
          }
          
          .children-cards-section > div > div {
            width: 100%;
          }
          
          /* How it works section changes */
          .how-it-works {
            padding: 1rem;
          }
          
          .how-it-works img {
            width: 100%;
            height: auto;
          }
          
          /* Sponsors section changes */
          .sponsors-section {
           overflow: hidden;
            padding: 1rem;
            

          }
          
          .sponsors-section > div {
            display: flex;
            
            gap: 1rem;
            padding: 1rem 0;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          
          .sponsors-section > div > div {
            flex: 0 0 auto;
            width: 280px;
            scroll-snap-align: start;
          }
          
          /* Footer changes */
          footer {
            padding: 1rem;
          }
          
          footer > div {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          
          /* Custom kid card changes */
          .custom-kid-card {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            min-height: 540px !important;
            height: 540px !important;
            margin: 0 auto;
          }
          .custom-kid-card > div:first-child {
            height: 270px !important;
          }
          .custom-kid-card > div:last-child {
            margin-top: 270px !important;
            height: 176px !important;
            padding: 0 1.2rem 1.2rem 1.2rem !important;
          }
          /* Swiper slide adjustments */
          .swiper-slide {
     
            min-width: 315px !important;
          
            padding-left: 12px !important;
            padding-right: 12px !important;
            box-sizing: border-box;
          }
          /* Add padding to the cards section for mobile */
          .children-cards-section {
            padding: 0 12px;
          }
        }
        /* Desktop Swiper slide width */
        @media (min-width: 769px) {
          .swiper-slide {
            width: 260px !important;
            min-width: 260px !important;
            max-width: 260px !important;
          }
        }
        
        /* WhatsApp button responsive styles */
        @media (max-width: 480px) {
          a[href*="wa.me"] {
            width: 48px !important;
            height: 48px !important;
            bottom: 78px !important;
            right: 20px !important;
          }
          a[href*="wa.me"] svg {
            width: 28px !important;
            height: 28px !important;
          }
        }
        
        @media (min-width: 481px) and (max-width: 768px) {
          a[href*="wa.me"] {
            width: 52px !important;
            height: 52px !important;
            bottom: 78px !important;
            right: 24px !important;
          }
          a[href*="wa.me"] svg {
            width: 30px !important;
            height: 30px !important;
          }
        }
      `}</style>

      {/* Add MobileFooter at the end */}
      <MobileFooter />
    </div>
  );
};

export default ShaagirdKidsComponent;
